import { EnquiryHandle, Product, RightPanelTabs, RightPanelTabsConfiguration } from '@access-erp/common';
import { ActionCardProps, ActionsProps, CardType, IRowNode } from '@access-erp/ui-components';
import { useEffect, useMemo, useState } from 'react';

import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import History from '../components/History/History';
import { GetPaginatedFilteredPurchaseOrders }
  from '@/graphql/contracts/_generated_/getPaginatedFilteredPurchaseOrders.query';

type PurchaseOrderWithId = GetPaginatedFilteredPurchaseOrders & {
  id: string;
};

interface UseRightPanelParams {
  selectedHeaderRows: IRowNode<PurchaseOrderWithId>[];
  gridRef?: EnquiryHandle<PurchaseOrderWithId>;
}

type ActionButton = {
  config: ActionCardProps;
  isVisible: boolean;
};

export function useRightPanel({ selectedHeaderRows }: UseRightPanelParams) {
  const [isVisibleAddModal, setIsVisibleAddModal] = useState(false);
  const [isVisibleEditModal, setIsVisibleEditModal] = useState(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] = useState(false);
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };
    getRole();
  }, [getUserRole]);

  const isReaderRole = userRole === UserRole.READER;

  const actionButtons = useMemo<ActionButton[]>(
    () => [
      {
        config: {
          text: 'Add Purchase Order',
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: () => setIsVisibleAddModal(true),
        },
        isVisible: !isReaderRole,
      },
      {
        config: {
          text: 'Edit Purchase Order',
          cardType: CardType.ACTION,
          product: Product.ContractsAndBilling,
          onClick: () => setIsVisibleEditModal(true),
        },
        isVisible: !isReaderRole && selectedHeaderRows.length == 1,
      },
      {
        config: {
          text: 'Delete Purchase Order',
          cardType: CardType.DELETE,
          product: Product.ContractsAndBilling,
          onClick: () => setIsVisibleDeleteModal(true),
        },
        isVisible: !isReaderRole && selectedHeaderRows.length == 1,
      },
    ],
    [selectedHeaderRows, isReaderRole],
  );

  const actionsParams: ActionsProps = {
    actionCards: {
      actions: actionButtons.filter((el) => el.isVisible).map((el) => el.config),
    },
  };

  const tabsConfiguration = useMemo<RightPanelTabsConfiguration>(
    () => (
      {
        [RightPanelTabs.ACTIONS]: actionsParams,
        History: {
          component: <History selectedPurchaseOrders={selectedHeaderRows} />,
        },
      }
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedHeaderRows],
  );

  return {
    tabsConfiguration,
    isVisibleAddModal: isVisibleAddModal,
    setIsVisibleAddModal: setIsVisibleAddModal,
    isVisibleEditModal: isVisibleEditModal,
    setIsVisibleEditModal: setIsVisibleEditModal,
    isVisibleDeleteModal: isVisibleDeleteModal,
    setIsVisibleDeleteModal: setIsVisibleDeleteModal,
  };
}
